import React, {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import i18n from "i18next";
import useCache from "../../hooks/useCache";
import { useTooltipWordsData } from "../../hooks/useTooltipWordsData";
import { USE_CACHE } from "../../constants";
import {
  checkIfLevelIsLocked,
  handleDidCompleteLevel,
  handleLevelClick,
  handleLevelType,
  reloadCurrentCourse,
} from "./EducationModel";
import { useFetchCourseData } from "./useFetchCourseData";
import { readLevelCompletionAnimationDebugFlag } from "../../util/levelCompletionAnimationDebugFlag";
import { syncDisplayedLevelProgress } from "./syncDisplayedLevelProgress";
import { levelTypeToTheoryType } from "./Education.functions";
import { createLevelModalLoadingShell } from "./openLevelModalLoadingShell";
import { COURSE_REVIEW_REVEAL_DELAY_MS } from "./courseReviewRevealDelayMs";
import {
  type CourseData,
  type LevelData,
  type EducationProviderProps,
  TheoryTypes,
  type LevelComponentProps,
  type EducationContextType,
  GenericLevelId,
  type Course,
  type ReloadCourseDataProps,
} from "./Education.types";

const EducationContext = createContext<EducationContextType>({
  courseCompleted: false,
  courseData: null,
  diagnosticMessage: undefined,
  totalLevels: 0,
  sortedLevels: [],
  levelInfo: null,
  lockedPopupMessage: undefined,
  highestLevelCompleted: 0,
  showQuizSuccessPopup: false,
  showQuizFailedPopup: false,
  totalCorrectAnswers: null,
  totalAnswers: null,
  loading: true,
  courseDataError: false,
  fadeIn: false,
  levelComponentProps: {
    selectedLevelType: null,
    levelData: null,
    levelInfo: null,
    levelCompleted: () => {},
    closePopupWrapper: () => {},
    quizSuccess: () => {},
    quizFailed: () => {},
    isLoading: false,
    loadError: null,
    retryLoad: () => {},
    ids: null,
    isFirstAIChatLevel: false,
    currentWordlistIndex: 0,
    setCurrentWordlistIndex: () => {},
    currentQuestionIndex: 0,
    setCurrentQuestionIndex: () => {},
  },
  cacheLevel: async () => {},
  setTotalLevels: () => {},
  setSortedLevels: () => {},
  setCourseCompleted: () => {},
  setFadeIn: () => {},
  reloadCourseData: async () => {},
  handleLevelHover: async () => {},
  handleLevelClickWrapper: async () => {},
  closeLockedPopup: () => {},
  handleDidCompleteLevelWrapper: async () => {},
  closeSuccessFailedPopups: () => {},
  handleCloseSuccessPopup: () => {},
  setDiagnosticMessage: () => {},
  tempQuestionAnswerStates: [],
  setTempQuestionAnswerStates: () => {},
  updateBackendOnProgress: async () => false,
  clickedLevelRect: null,
  setClickedLevelRect: () => {},
  triggerLevelCompletionSequence: () => {},
  levelCompletionAnimation: null,
  triggerCourseCompletedConfetti: false,
  setTriggerCourseCompletedConfetti: () => {},
  isRevealingCourseCompletion: false,
  revealCourseCompletion: () => {},
  getPendingCompletedLevel: () => null,
  clearPendingCompletedLevel: () => {},
});

// TODO: Split into smaller contexts (Course, Page, Popup, ModalView)

const EducationProvider: React.FC<EducationProviderProps> = ({
  children,
  onCertificateGenerated,
}) => {
  const [appendToCache, fetchFromCache, checkCache] = useCache<LevelData>();

  // TODO: Bundle below into a single object
  const [courseData, setCourseData] = useState<CourseData | null>(null);

  // Load tooltip words for the current course
  // This populates the global tooltip registry used by processHTMLWithTooltips
  useTooltipWordsData(courseData?.ID ?? null);
  const [highestLevelCompleted, setHighestLevelCompleted] = useState<number>(0);
  const [totalLevels, setTotalLevels] = useState<number>(0);
  const [sortedLevels, setSortedLevels] = useState<Level[]>([]);
  const [selectedLevelType, setSelectedLevelType] = useState<LevelType | null>(
    null
  );
  const [selectedId, setSelectedId] = useState<GenericLevelId>(null);
  const levelIDRef = useRef<string | null>(null);
  const [levelInfo, setLevelInfo] = useState<Level | null>(null);
  const [currentWordlistIndex, setCurrentWordlistIndex] = useState<number>(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [lockedPopupMessage, setLockedPopupMessage] = useState<string | null>();
  const [showQuizSuccessPopup, setShowQuizSuccessPopup] =
    useState<boolean>(false);
  const [showQuizFailedPopup, setShowQuizFailedPopup] =
    useState<boolean>(false);
  const [totalCorrectAnswers, setTotalCorrectAnswers] = useState<number | null>(
    null
  );
  const [totalAnswers, setTotalAnswers] = useState<number | null>(null);
  const [courseCompleted, setCourseCompleted] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [courseDataError, setCourseDataError] = useState<boolean>(false);
  const [diagnosticMessage, setDiagnosticMessage] = useState<
    string | undefined
  >();
  const [fadeIn, setFadeIn] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [levelData, setLevelData] = useState<unknown | null>(null);
  const [tempQuestionAnswerStates, setTempQuestionAnswerStates] =
    useState<QuestionAnswerStates>([]);
  const [levelStartTime, setLevelStartTime] = useState<number | null>(null);
  const [clickedLevelRect, setClickedLevelRect] = useState<DOMRect | null>(
    null
  );
  const [triggerCourseCompletedConfetti, setTriggerCourseCompletedConfetti] =
    useState<boolean>(false);
  const [levelCompletionAnimation, setLevelCompletionAnimation] = useState<{
    completedLevelIndex: number;
    signalId: number;
    advanceToNextLevel: boolean;
  } | null>(null);
  const completionAnimationSignalRef = useRef(0);
  const [isRevealingCourseCompletion, setIsRevealingCourseCompletion] =
    useState<boolean>(false);
  const pendingCompletedLevelRef = useRef<{
    level: Level;
    isLastLevel: boolean;
  } | null>(null);
  const pendingDiagnosticReloadRef = useRef(false);
  const revealCourseCompletionTimerRef = useRef<number | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const lastLoadAttemptRef = useRef<{ course: Course; level: Level } | null>(
    null
  );
  const { fetchCourseData } = useFetchCourseData(courseData?.ID);

  // Timeout constant for fetch operations (30 seconds)
  const FETCH_TIMEOUT_MS = 30000;

  // Syncs the highest level completed with the course data after it has been
  // fetched.
  useEffect(() => {
    if (courseData) {
      setHighestLevelCompleted((displayedHighest) =>
        syncDisplayedLevelProgress({
          displayedHighest,
          fetchedHighest: courseData.highestLevelCompleted,
          pendingCompletedLevelIndex:
            pendingCompletedLevelRef.current?.level.TotalLevelIndex ?? null,
        })
      );
    }
  }, [setHighestLevelCompleted, courseData]);

  const updateCourseData = useCallback(
    async (
      selectedLevelType: LevelType | null,
      ids: GenericLevelId,
      level: Level
    ) => {
      setIsLoading(true);
      setLoadError(null);
      setSelectedLevelType(selectedLevelType);
      setSelectedId(ids);
      setCurrentWordlistIndex(0); // Reset wordlist index for new level
      setCurrentQuestionIndex(0); // Reset question index for new level

      // Save start time when level opens (early, before any async operations)
      const startTime = Date.now();
      setLevelStartTime(startTime);
      // Also save to localStorage as backup
      if (level?.ID) {
        localStorage.setItem(`levelStart_${level.ID}`, startTime.toString());
      }

      if (!selectedLevelType) {
        // Clear start time when level is closed
        if (level?.ID) {
          localStorage.removeItem(`levelStart_${level.ID}`);
        }
        setLevelStartTime(null);
        setLevelData(null);
        setIsLoading(false);
        setSelectedId(null);
        return;
      }

      try {
        // Create timeout promise
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => {
            reject(new Error("TIMEOUT"));
          }, FETCH_TIMEOUT_MS);
        });

        // Race between fetch and timeout
        const result = await Promise.race([
          fetchCourseData(selectedLevelType, ids),
          timeoutPromise,
        ]);

        const levelID = levelIDRef.current;

        // Discard the result if the level ID has changed
        if (level.ID !== levelID) {
          console.info(
            "Level ID has changed, discarding result",
            level.ID,
            levelID
          );
          // Reset loading state when discarding result
          setIsLoading(false);
          return;
        }

        setIsLoading(false);
        setLevelData(result);
      } catch (error) {
        console.error("Failed to fetch level data:", error);
        setIsLoading(false);
        setLevelData(null);

        // Set user-friendly error message
        if (error instanceof Error && error.message === "TIMEOUT") {
          setLoadError(
            "Laddningen tog för lång tid. Kontrollera din internetanslutning och försök igen."
          );
        } else {
          setLoadError("Kunde inte ladda nivån. Försök igen.");
        }
      }
    },
    [fetchCourseData, FETCH_TIMEOUT_MS]
  );

  const reloadCourseData = useCallback(
    async ({ courseToLoad, isSilent = false }: ReloadCourseDataProps) => {
      if (!isSilent) {
        setLoading(true);
        setFadeIn(false);
      }
      setCourseDataError(false);

      try {
        const updatedCourse = await reloadCurrentCourse(courseToLoad.ID);

        if (updatedCourse) {
          setCourseData(updatedCourse);
        }
      } catch (error) {
        // A failed fetch must not be mistaken for a reassigned course, so we
        // surface a retryable error instead of letting the UI redirect away.
        console.error("Failed to load course data:", error);
        setCourseDataError(true);
      } finally {
        if (!isSilent) {
          setLoading(false);
        } else {
          // Clear diagnostic message immediately for silent reloads
          setDiagnosticMessage(undefined);
        }
      }

      if (!isSilent) {
        setTimeout(() => {
          setDiagnosticMessage(undefined);
        }, 500);
      }
    },
    [setDiagnosticMessage, setLoading, setFadeIn, setCourseData]
  );

  const incrementHighestLevelCompleted = useCallback(
    (level: Level) => {
      if (level.TotalLevelIndex > highestLevelCompleted) {
        setHighestLevelCompleted(level.TotalLevelIndex);
      }
    },
    [highestLevelCompleted, setHighestLevelCompleted]
  );

  /**
   * Handle the hover event for a level
   *
   * @param {Object} level - The level object
   */
  const handleLevelHover = async (level: Level) => {
    await cacheLevel(level);
  };

  /**
   * Fetches and stores the data of a level in the cache
   *
   * @param {Object} level - The level object
   *
   * @returns {Promise<void>}
   */
  const cacheLevel = useCallback(
    async (level: Level) => {
      if (!USE_CACHE) {
        // Skip pre-caching in developer mode to always fetch fresh data
        return;
      }
      const isCached = checkCache(level.ID);
      if (isCached) return;

      // Do not cache if the level is locked
      const isLocked = checkIfLevelIsLocked(
        level,
        courseData?.highestLevelCompleted ?? 0
      );

      if (isLocked) return;

      try {
        const genericId = await handleLevelType(level);
        const theoryType = levelTypeToTheoryType(level.LevelType);
        const data = await fetchCourseData(
          theoryType ?? TheoryTypes.THEORY,
          genericId
        );

        appendToCache(level.ID, {
          id: level.ID,
          selectedLevelType: theoryType ?? "theory",
          data: data,
          ids: genericId,
        });
      } catch (error) {
        // Preload failed silently - user can still load level normally
        console.warn("Failed to preload level:", level.ID, error);
      }
    },
    [appendToCache, checkCache, courseData, fetchCourseData]
  );

  /**
   * Tries to load the level data from the cache
   *
   * @param {Object} level - The level object
   *
   * @returns {Object | undefined} The level data
   */
  const loadFromCache = useCallback((cache: LevelData) => {
    setSelectedId(cache.ids);
    setLevelData(cache.data);
    setSelectedLevelType(cache.selectedLevelType);
    setIsLoading(false);
    setCurrentWordlistIndex(0); // Reset wordlist index for new level
    setCurrentQuestionIndex(0); // Reset question index for new level

    // Save start time when level opens (level data loaded from cache, but start time is always new)
    const startTime = Date.now();
    setLevelStartTime(startTime);
    // Also save to localStorage as backup
    if (cache.id) {
      localStorage.setItem(`levelStart_${cache.id}`, startTime.toString());
    }
  }, []);

  /**
   * Set the level data based on the response from the server
   */
  const handleLevelSelection = useCallback(
    (res: GenericLevelId, level: Level) => {
      const levelType = levelTypeToTheoryType(level.LevelType);
      updateCourseData(levelType, res, level);
    },
    [updateCourseData]
  );

  const loadLevel = useCallback(
    async (course: Course, level: Level) => {
      // Store for retry functionality
      lastLoadAttemptRef.current = { course, level };

      setLevelInfo(level);
      setLoadError(null);
      levelIDRef.current = level.ID;

      // Check if cache data is available
      const cachedLevelData = !USE_CACHE ? undefined : fetchFromCache(level.ID);

      if (cachedLevelData && cachedLevelData.id === level.ID) {
        loadFromCache(cachedLevelData);
        return;
      }

      const { selectedLevelType: shellLevelType } =
        createLevelModalLoadingShell(level);
      if (shellLevelType) {
        setSelectedLevelType(shellLevelType);
        setSelectedId(null);
        setLevelData(null);
        setIsLoading(true);
        setCurrentWordlistIndex(0);
        setCurrentQuestionIndex(0);
      }

      try {
        // Create timeout promise for handleLevelClick
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => {
            reject(new Error("TIMEOUT"));
          }, FETCH_TIMEOUT_MS);
        });

        const res = await Promise.race([
          handleLevelClick(course, level, highestLevelCompleted),
          timeoutPromise,
        ]);
        handleLevelSelection(res, level);
      } catch (error) {
        console.error("Failed to load level:", error);
        setIsLoading(false);

        // Set user-friendly error message
        if (error instanceof Error && error.message === "TIMEOUT") {
          setLoadError(
            "Laddningen tog för lång tid. Kontrollera din internetanslutning och försök igen."
          );
        } else {
          setLoadError("Kunde inte ladda nivån. Försök igen.");
        }
      }
    },
    [
      fetchFromCache,
      loadFromCache,
      setLevelInfo,
      handleLevelSelection,
      highestLevelCompleted,
      setIsLoading,
      FETCH_TIMEOUT_MS,
    ]
  );

  const retryLoadLevel = useCallback(() => {
    const lastAttempt = lastLoadAttemptRef.current;
    if (lastAttempt) {
      loadLevel(lastAttempt.course, lastAttempt.level);
    }
  }, [loadLevel]);

  const handleLevelClickWrapper = useCallback(
    async (course: Course, level: Level) => {
      const lockedMessage = checkIfLevelIsLocked(
        level,
        highestLevelCompleted ?? 0
      );

      if (lockedMessage) {
        setLockedPopupMessage(lockedMessage);
        return;
      }

      loadLevel(course, level);
    },
    [setLockedPopupMessage, loadLevel, highestLevelCompleted]
  );

  const quizSuccess = useCallback(
    (totalCorrectAnswers: number, totalAnswers: number) => {
      setTotalAnswers(totalAnswers);
      setTotalCorrectAnswers(totalCorrectAnswers);
      setShowQuizSuccessPopup(true);
    },
    [setTotalAnswers, setTotalCorrectAnswers, setShowQuizSuccessPopup]
  );

  const quizFailed = useCallback(
    (totalCorrectAnswers: number, totalAnswers: number) => {
      setTotalAnswers(totalAnswers);
      setTotalCorrectAnswers(totalCorrectAnswers);
      setShowQuizFailedPopup(true);
    },
    [setTotalAnswers, setTotalCorrectAnswers, setShowQuizFailedPopup]
  );

  const closePopupWrapper = useCallback(() => {
    // Clear start time when level is closed to prevent using old time if reopened later
    const currentLevelId = levelIDRef.current;
    if (currentLevelId) {
      localStorage.removeItem(`levelStart_${currentLevelId}`);
    }
    setLevelStartTime(null);

    setSelectedLevelType(null);
    setLevelData(null);
    levelIDRef.current = null;
    setLevelInfo(null);
    setIsLoading(false);
    setLoadError(null);
    lastLoadAttemptRef.current = null;
    setCurrentWordlistIndex(0); // Reset wordlist index when level is closed
    setCurrentQuestionIndex(0); // Reset question index when level is closed
    setClickedLevelRect(null);

    if (pendingDiagnosticReloadRef.current) {
      setDiagnosticMessage(i18n.t("diagnosticTest.loadingLevels.text"));
    }
  }, [setSelectedLevelType, setLevelData, setLevelInfo, setIsLoading, setDiagnosticMessage]);

  const closeLockedPopup = useCallback(() => {
    setLockedPopupMessage(null);
  }, [setLockedPopupMessage]);

  const updateBackendOnProgress = useCallback(
    async (
      course: Course,
      level: Level,
      questionAnswerStates: QuestionAnswerStates
    ) => {
      if (!courseData) {
        console.error("Course data is not available");
        return false;
      }

      if (readLevelCompletionAnimationDebugFlag()) {
        console.info(
          "[level completion preview] Skipping Firebase save — reload the page to reset progress."
        );
        return true;
      }

      // Get start time from state or localStorage
      let startTime = levelStartTime;
      if (!startTime && level?.ID) {
        const savedTime = localStorage.getItem(`levelStart_${level.ID}`);
        if (savedTime) {
          startTime = parseInt(savedTime, 10);
        }
      }

      return await handleDidCompleteLevel(
        level,
        course,
        courseData,
        totalLevels,
        questionAnswerStates,
        startTime || undefined
      );
    },
    [courseData, totalLevels, levelStartTime]
  );

  // Runs once the modal has closed; the snake path choreographs the celebration itself.
  const triggerLevelCompletionSequence = useCallback(
    (level: Level, isLastLevel: boolean) => {
      const advanceToNextLevel =
        level.TotalLevelIndex > highestLevelCompleted;
      completionAnimationSignalRef.current += 1;
      setLevelCompletionAnimation({
        completedLevelIndex: level.TotalLevelIndex,
        signalId: completionAnimationSignalRef.current,
        advanceToNextLevel,
      });
      if (isLastLevel && advanceToNextLevel) {
        setCourseCompleted(true);
        setIsRevealingCourseCompletion(true);
      }
      incrementHighestLevelCompleted(level);
    },
    [highestLevelCompleted, incrementHighestLevelCompleted, setCourseCompleted]
  );

  const revealCourseCompletion = useCallback(() => {
    setTriggerCourseCompletedConfetti(true);

    if (revealCourseCompletionTimerRef.current != null) {
      window.clearTimeout(revealCourseCompletionTimerRef.current);
    }
    revealCourseCompletionTimerRef.current = window.setTimeout(() => {
      revealCourseCompletionTimerRef.current = null;
      setIsRevealingCourseCompletion(false);
    }, COURSE_REVIEW_REVEAL_DELAY_MS);
  }, []);

  useEffect(() => {
    setIsRevealingCourseCompletion(false);
    if (revealCourseCompletionTimerRef.current != null) {
      window.clearTimeout(revealCourseCompletionTimerRef.current);
      revealCourseCompletionTimerRef.current = null;
    }
  }, [courseData?.ID]);

  useEffect(
    () => () => {
      if (revealCourseCompletionTimerRef.current != null) {
        window.clearTimeout(revealCourseCompletionTimerRef.current);
      }
    },
    [],
  );

  const handleDidCompleteLevelWrapper = useCallback(
    async (
      course: Course,
      level: Level,
      questionAnswerStates: QuestionAnswerStates
    ) => {
      // Check if this is the last level - if so, set courseCompleted immediately
      // so the user sees the completion screen right away
      const effectiveTotalLevels = totalLevels || sortedLevels.length;
      const isLastLevel =
        effectiveTotalLevels > 0 &&
        level.TotalLevelIndex + 1 >= effectiveTotalLevels;
      const isAnimationPreview = readLevelCompletionAnimationDebugFlag();
      const isDiagnosticReload =
        level.LevelType === "diagnosticTest" && !isAnimationPreview;

      if (isDiagnosticReload) {
        pendingDiagnosticReloadRef.current = true;
      }

      // Save level info for the completion sequence
      // The sequence will be triggered when the modal closes (after animation)
      pendingCompletedLevelRef.current = {
        level,
        isLastLevel: isAnimationPreview ? false : isLastLevel,
      };

      let success = false;
      try {
        success = await updateBackendOnProgress(
          course,
          level,
          questionAnswerStates
        );
      } catch (error) {
        pendingDiagnosticReloadRef.current = false;
        if (isLastLevel) {
          setCourseCompleted(false);
        }
        throw error;
      }

      // Clear start time after level completion to prevent reuse
      if (level?.ID) {
        localStorage.removeItem(`levelStart_${level.ID}`);
      }
      setLevelStartTime(null);

      if (success) {
        if (!isAnimationPreview) {
          await reloadCourseData({ courseToLoad: course, isSilent: true });
          window.dispatchEvent(
            new CustomEvent("refreshMenuCourses", { detail: { silent: true } }),
          );
        }
      } else {
        pendingDiagnosticReloadRef.current = false;
        // Remove the loading screen if unable to update backend
        setDiagnosticMessage(undefined);
        // If failed and was last level, revert courseCompleted
        if (isLastLevel) {
          setCourseCompleted(false);
        }
      }

      if (isDiagnosticReload) {
        pendingDiagnosticReloadRef.current = false;
        setDiagnosticMessage(undefined);
      }

      setTotalAnswers(0);
      setTotalCorrectAnswers(0);
    },
    [
      reloadCourseData,
      setTotalAnswers,
      setTotalCorrectAnswers,
      updateBackendOnProgress,
      totalLevels,
      sortedLevels,
      setCourseCompleted,
    ]
  );

  const closeSuccessFailedPopups = useCallback(() => {
    closePopupWrapper();
    setShowQuizSuccessPopup(false);
    setShowQuizFailedPopup(false);
  }, [closePopupWrapper, setShowQuizSuccessPopup, setShowQuizFailedPopup]);

  const handleCloseSuccessPopup = useCallback(() => {
    closePopupWrapper();
  }, [closePopupWrapper]);

  const levelCompleted = useCallback(
    (showPopup: boolean) => {
      if (!showPopup) {
        handleCloseSuccessPopup();
      }
    },
    [handleCloseSuccessPopup]
  );

  // Calculate if this is the first AI chat level
  const isFirstAIChatLevel = React.useMemo(() => {
    if (!levelInfo || levelInfo.LevelType !== "AIChat") {
      return false;
    }

    const previousAIChatLevels = sortedLevels.filter(
      (l: Level) =>
        l.LevelType === "AIChat" &&
        l.TotalLevelIndex < levelInfo.TotalLevelIndex
    );
    return previousAIChatLevels.length === 0;
  }, [levelInfo, sortedLevels]);

  const levelComponentProps: LevelComponentProps = {
    selectedLevelType,
    levelData,
    levelInfo,
    levelCompleted,
    closePopupWrapper,
    quizSuccess,
    quizFailed,
    isLoading,
    loadError,
    retryLoad: retryLoadLevel,
    ids: selectedId,
    isFirstAIChatLevel,
    currentWordlistIndex,
    setCurrentWordlistIndex,
    currentQuestionIndex,
    setCurrentQuestionIndex,
  };

  const exposedState = {
    courseCompleted,
    courseData,
    diagnosticMessage,
    totalLevels,
    sortedLevels,
    levelInfo,
    lockedPopupMessage,
    highestLevelCompleted,
    showQuizSuccessPopup,
    showQuizFailedPopup,
    totalCorrectAnswers,
    totalAnswers,
    loading,
    courseDataError,
    fadeIn,
    levelComponentProps,
    cacheLevel,
    setTotalLevels,
    setSortedLevels,
    setCourseCompleted,
    setFadeIn,
    reloadCourseData,
    handleLevelHover,
    handleLevelClickWrapper,
    closeLockedPopup,
    handleDidCompleteLevelWrapper,
    closeSuccessFailedPopups,
    handleCloseSuccessPopup,
    setDiagnosticMessage,
    tempQuestionAnswerStates,
    setTempQuestionAnswerStates,
    updateBackendOnProgress,
    onCertificateGenerated,
    clickedLevelRect,
    setClickedLevelRect,
    triggerLevelCompletionSequence,
    levelCompletionAnimation,
    triggerCourseCompletedConfetti,
    setTriggerCourseCompletedConfetti,
    isRevealingCourseCompletion,
    revealCourseCompletion,
    getPendingCompletedLevel: () => pendingCompletedLevelRef.current,
    clearPendingCompletedLevel: () => {
      pendingCompletedLevelRef.current = null;
    },
  };

  return (
    <EducationContext.Provider value={exposedState}>
      {children}
    </EducationContext.Provider>
  );
};

export { EducationProvider, EducationContext };
